import { addressKey } from "./addressKey";

// Polygon geometry helpers for the map's boundary filter. Kept dependency-free
// (no turf) — at submarket scale a planar approximation with a latitude
// correction is well inside the accuracy we care about.

// Ray-casting point-in-polygon. `ring` is an array of [lat, lng] pairs and is
// treated as implicitly closed (last point connects back to the first).
export function pointInPolygon(lat, lng, ring) {
  if (!ring || ring.length < 3) return false;
  let inside = false;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const latI = ring[i][0], lngI = ring[i][1];
    const latJ = ring[j][0], lngJ = ring[j][1];
    const straddles = (latI > lat) !== (latJ > lat);
    if (straddles && lng < ((lngJ - lngI) * (lat - latI)) / (latJ - latI) + lngI) {
      inside = !inside;
    }
  }
  return inside;
}

// [[southLat, westLng], [northLat, eastLng]] — feeds Leaflet's fitBounds.
export function polygonBounds(ring) {
  if (!ring || ring.length === 0) return null;
  const lats = ring.map(p => p[0]), lngs = ring.map(p => p[1]);
  return [[Math.min(...lats), Math.min(...lngs)], [Math.max(...lats), Math.max(...lngs)]];
}

// Meters per degree at a given latitude (WGS84 series approximation).
function metersPerDegree(lat) {
  const r = (lat * Math.PI) / 180;
  return {
    lat: 111132.92 - 559.82 * Math.cos(2 * r) + 1.175 * Math.cos(4 * r),
    lng: 111412.84 * Math.cos(r) - 93.5 * Math.cos(3 * r),
  };
}

// Shoelace area, projected to meters at the ring's mean latitude.
export function polygonAreaSqMi(ring) {
  if (!ring || ring.length < 3) return 0;
  const latAvg = ring.reduce((s, p) => s + p[0], 0) / ring.length;
  const m = metersPerDegree(latAvg);
  let doubled = 0;
  for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
    const xi = ring[i][1] * m.lng, yi = ring[i][0] * m.lat;
    const xj = ring[j][1] * m.lng, yj = ring[j][0] * m.lat;
    doubled += xj * yi - xi * yj;
  }
  return Math.abs(doubled) / 2 / 2589988.110336;
}

// Great-circle distance in metres. Used to tie comps to a building when the
// address strings differ.
export function metersBetween(aLat, aLng, bLat, bLng) {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLng = ((bLng - aLng) * Math.PI) / 180;
  const la1 = (aLat * Math.PI) / 180, la2 = (bLat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLng / 2) ** 2;
  return 2 * R * Math.asin(Math.sqrt(h));
}

// Same building? Uses the app's canonical addressKey so "6399 N 142nd Ave"
// and "6399 North 142nd Avenue" agree — but addressKey deliberately drops
// city/state, so "100 Main St" in Tampa and in Naples share a key. Where both
// records are geocoded we require them to be within SAME_BUILDING_M as well,
// which separates them without needing locality to be spelled consistently.
const SAME_BUILDING_M = 2000;

export function sameBuilding(aAddr, aLat, aLng, bAddr, bLat, bLng) {
  const ka = addressKey(aAddr), kb = addressKey(bAddr);
  if (!ka || ka !== kb) return false;
  if (aLat != null && aLng != null && bLat != null && bLng != null) {
    return metersBetween(aLat, aLng, bLat, bLng) <= SAME_BUILDING_M;
  }
  return true;
}

// Lease comps recorded at a building — same address, or within
// `radiusMeters` when the address strings differ entirely.
export function leaseCompsNear(comps, pin, radiusMeters = 200) {
  if (!pin) return [];
  return (comps || [])
    .filter(c => c.type === "lease")
    .filter(c => {
      if (sameBuilding(pin.address, pin.lat, pin.lng, c.address, c.latitude, c.longitude)) return true;
      if (pin.lat && pin.lng && c.latitude && c.longitude) {
        return metersBetween(pin.lat, pin.lng, c.latitude, c.longitude) <= radiusMeters;
      }
      return false;
    })
    .sort((a, b) => String(b.comp_date || "").localeCompare(String(a.comp_date || "")));
}

// Sale comps at a building — same match, no proximity fallback: a sale down
// the street is a different asset, not another record for this one.
export function saleCompsAt(comps, pin) {
  if (!pin) return [];
  return (comps || [])
    .filter(c => c.type === "sale")
    .filter(c => sameBuilding(pin.address, pin.lat, pin.lng, c.address, c.latitude, c.longitude))
    .sort((a, b) => String(b.comp_date || "").localeCompare(String(a.comp_date || "")));
}
